import maplibregl from "maplibre-gl";
import type { GeoJSON } from "geojson";
import type { Marker } from "@/types";
import type { Bounds, Location } from "@/type/api";

const STADIA_STYLE_BASE = "https://tiles.stadiamaps.com/styles/";

export function mapStyleURL(style: string): string {
	return `${STADIA_STYLE_BASE}${style}.json`;
}

export function boundsDefault(): maplibregl.LngLatBounds {
	return new maplibregl.LngLatBounds(
		new maplibregl.LngLat(-125, 50),
		new maplibregl.LngLat(-70, 25),
	);
}

export function boundsForServiceArea(b?: Bounds): maplibregl.LngLatBounds {
	if (b) {
		return boundsFromAPI(b);
	} else {
		return boundsDefault();
	}
}
export function boundsFromAPI(b: Bounds): maplibregl.LngLatBounds {
	return new maplibregl.LngLatBounds(
		new maplibregl.LngLat(b.min.longitude, b.max.latitude),
		new maplibregl.LngLat(b.max.longitude, b.min.latitude),
	);
}

export function boundsMarkers(markers: Marker[]): maplibregl.LngLatBounds {
	let min_lat = 90;
	let min_lng = 180;
	let max_lat = -90;
	let max_lng = -180;
	markers.forEach((marker: Marker) => {
		min_lat = Math.min(marker.location.latitude, min_lat);
		min_lng = Math.min(marker.location.longitude, min_lng);
		max_lat = Math.max(marker.location.latitude, max_lat);
		max_lng = Math.max(marker.location.longitude, max_lng);
	});
	return new maplibregl.LngLatBounds(
		new maplibregl.LngLat(min_lng, min_lat),
		new maplibregl.LngLat(max_lng, max_lat),
	);
}
export function boundsWithPadding(
	min: Location,
	max: Location,
	padding: number,
) {
	return new maplibregl.LngLatBounds(
		new maplibregl.LngLat(min.longitude - padding, min.latitude - padding),
		new maplibregl.LngLat(max.longitude + padding, max.latitude + padding),
	);
}
// Helper functions (outside component)
const getBoundingBox = (points: Location[]) => {
	if (!points || points.length === 0) {
		return null;
	}

	let maxLat = points[0].latitude;
	let maxLng = points[0].longitude;
	let minLat = points[0].latitude;
	let minLng = points[0].longitude;

	for (const point of points) {
		if (point.latitude < minLat) minLat = point.latitude;
		if (point.latitude > maxLat) maxLat = point.latitude;
		if (point.longitude < minLng) minLng = point.longitude;
		if (point.longitude > maxLng) maxLng = point.longitude;
	}

	return new window.maplibregl.LngLatBounds(
		new window.maplibregl.LngLat(minLng, minLat),
		new window.maplibregl.LngLat(maxLng, maxLat),
	);
};

export function boundsFromGeoJSON(
	geojson: GeoJSON.GeoJSON,
	bufferPercent: number = 0,
): maplibregl.LngLatBounds | null {
	const coords: [number, number][] = [];
	collectCoords(geojson, coords);
	if (coords.length === 0) return null;

	let minLng = coords[0][0],
		maxLng = coords[0][0];
	let minLat = coords[0][1],
		maxLat = coords[0][1];
	for (const [lng, lat] of coords) {
		if (lng < minLng) minLng = lng;
		if (lng > maxLng) maxLng = lng;
		if (lat < minLat) minLat = lat;
		if (lat > maxLat) maxLat = lat;
	}

	if (bufferPercent > 0) {
		const lngPad = (maxLng - minLng) * (bufferPercent / 100);
		const latPad = (maxLat - minLat) * (bufferPercent / 100);
		minLng -= lngPad;
		maxLng += lngPad;
		minLat -= latPad;
		maxLat += latPad;
	}

	return new maplibregl.LngLatBounds(
		new maplibregl.LngLat(minLng, minLat),
		new maplibregl.LngLat(maxLng, maxLat),
	);
}

function collectCoords(obj: any, out: [number, number][]): void {
	if (!obj || typeof obj !== "object") return;
	if (Array.isArray(obj)) {
		if (
			obj.length === 2 &&
			typeof obj[0] === "number" &&
			typeof obj[1] === "number"
		) {
			out.push([obj[0], obj[1]]);
			return;
		}
		for (const item of obj) collectCoords(item, out);
		return;
	}
	if (obj.coordinates) collectCoords(obj.coordinates, out);
	if (obj.geometry) collectCoords(obj.geometry, out);
	if (obj.features) collectCoords(obj.features, out);
}

/**
 * Ray-casting point-in-polygon test. `ring` is a closed or open list of
 * [lng, lat] pairs; both forms work (the test treats the ring as cyclic).
 */
export function pointInPolygon(
	lng: number,
	lat: number,
	ring: [number, number][],
): boolean {
	let inside = false;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const [xi, yi] = ring[i];
		const [xj, yj] = ring[j];
		const intersects =
			yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
		if (intersects) inside = !inside;
	}
	return inside;
}
